import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AlertCircle, ArrowLeft, Lock } from 'lucide-react';

import Button from 'components/_UI/Button';
import ImageUpload from 'components/_UI/ImageUpload';
import Loader from 'components/_UI/Loader';
import colors from 'theme/colors';
import paths from 'routes/paths';
import { Link } from 'react-router-dom';
import { FullyCentered } from 'styled/alignment/Center';

import {
    Actions,
    InfoBox,
    InfoText,
    InfoTitle,
    StepError,
    StepShell,
    StepSubtitle,
    StepTitle,
} from '../partnerStyles';

const ACCEPTED_FILES = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'application/pdf': ['.pdf'],
};

const MAX_SIZE_MB = 10;

/**
 * Passo 3 — documento, termos e envio.
 *
 * O documento era pedido no passo 2, antes de a pessoa ter investido qualquer
 * coisa no cadastro: é o pedido de maior atrito do fluxo e ficava no meio dele.
 * Aqui ele fecha o processo, ao lado do aceite e do botão de enviar.
 */
export default function StepDocuments({
    documents,
    onDocumentsChange,
    onSubmit,
    onBack,
    submitting,
    documentErrors,
    submitError,
}) {
    const titleRef = useRef(null);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [localErrors, setLocalErrors] = useState({ front: '', back: '', terms: '' });

    useEffect(() => {
        titleRef.current?.focus();
    }, []);

    const previews = usePreviews(documents);

    const errors = useMemo(() => ({
        front: documentErrors?.front || localErrors.front,
        back: documentErrors?.back || localErrors.back,
        terms: localErrors.terms,
    }), [documentErrors, localErrors]);

    function setFile(side, file) {
        setLocalErrors((prev) => ({ ...prev, [side]: '' }));
        onDocumentsChange({ ...documents, [side]: file });
    }

    function handleSubmit(event) {
        event.preventDefault();

        /* O botão continua clicável de propósito. Antes ele ficava desabilitado
           até tudo estar certo, e a mensagem que explicava o que faltava era
           inalcançável — a pessoa via um botão morto e nenhuma pista. */
        const nextErrors = {
            front: documents?.front ? '' : 'Envie a frente do documento.',
            back: documents?.back ? '' : 'Envie o verso do documento.',
            terms: acceptedTerms ? '' : 'É preciso aceitar os termos para enviar o cadastro.',
        };

        setLocalErrors(nextErrors);

        if (nextErrors.front || nextErrors.back || nextErrors.terms) {
            return;
        }

        onSubmit();
    }

    return (
        <StepShell>
            <StepTitle ref={titleRef} tabIndex={-1}>
                Falta só confirmar quem é você
            </StepTitle>
            <StepSubtitle>
                Um documento com foto é o que nos permite liberar repasses no seu nome.
            </StepSubtitle>

            <InfoBox>
                <InfoTitle>
                    <Lock size={12} strokeWidth={2.5} aria-hidden='true' />
                    Por que pedimos isso
                </InfoTitle>
                <InfoText>
                    A imagem é usada só para conferir seus dados e cumprir as regras de
                    prevenção à fraude do meio de pagamento. Ela não aparece no seu perfil
                    nem para quem compra.
                </InfoText>
            </InfoBox>

            <form onSubmit={handleSubmit} noValidate>
                <UploadGrid>
                    <UploadSlot
                        side='front'
                        title='Frente do RG ou CNH'
                        hint='A foto e o número precisam estar legíveis.'
                        file={documents?.front}
                        preview={previews.front}
                        error={errors.front}
                        onFile={(file) => setFile('front', file)}
                        onReject={(message) => setLocalErrors((prev) => ({ ...prev, front: message }))}
                    />

                    <UploadSlot
                        side='back'
                        title='Verso do RG ou CNH'
                        hint='Sem cortes nas bordas e sem reflexo.'
                        file={documents?.back}
                        preview={previews.back}
                        error={errors.back}
                        onFile={(file) => setFile('back', file)}
                        onReject={(message) => setLocalErrors((prev) => ({ ...prev, back: message }))}
                    />
                </UploadGrid>

                <FormatHint>JPG, PNG ou PDF, até {MAX_SIZE_MB} MB por arquivo.</FormatHint>

                <TermsRow>
                    <TermsCheckbox
                        type='checkbox'
                        id='partner-terms'
                        checked={acceptedTerms}
                        onChange={() => {
                            setAcceptedTerms((prev) => !prev);
                            setLocalErrors((prev) => ({ ...prev, terms: '' }));
                        }}
                    />
                    {/* `htmlFor`, não `for`: com o atributo errado o React não associava
                        label e input, e clicar no texto não marcava nada. */}
                    <TermsLabel htmlFor='partner-terms'>
                        Li e concordo com os{' '}
                        <TermsLink target='_blank' to={`${paths.terms.useTerms}?createPartner=1`}>
                            Termos de Uso
                        </TermsLink>{' '}
                        e a{' '}
                        <TermsLink target='_blank' to={paths.terms.policiePrivacy}>
                            Política de Privacidade
                        </TermsLink>.
                    </TermsLabel>
                </TermsRow>

                {errors.terms && (
                    <FieldError role='alert'>
                        <AlertCircle size={13} aria-hidden='true' />
                        {errors.terms}
                    </FieldError>
                )}

                {submitError && (
                    <StepError role='alert'>
                        <AlertCircle size={15} aria-hidden='true' />
                        {submitError}
                    </StepError>
                )}

                <Actions>
                    {submitting ? (
                        <FullyCentered>
                            <Loader />
                        </FullyCentered>
                    ) : (
                        <>
                            <Button category='squiredBorded' variant='featured' type='submit'>
                                Enviar cadastro
                            </Button>

                            <Button category='minimalDark' onClick={onBack}>
                                <ArrowLeft size={20} aria-hidden='true' /> &nbsp; Voltar
                            </Button>
                        </>
                    )}
                </Actions>
            </form>
        </StepShell>
    );
}

function UploadSlot({ side, title, hint, file, preview, error, onFile, onReject }) {
    const isImage = Boolean(file?.type?.startsWith('image/'));

    return (
        <SlotWrapper>
            {/* Instrução acima da caixa: embaixo, ela só era lida depois de a pessoa
                já ter aberto o seletor sem saber o que enviar. */}
            <SlotTitle>{title}</SlotTitle>
            <SlotHint>{hint}</SlotHint>

            <SlotBox $invalid={Boolean(error)}>
                <ImageUpload
                    product
                    icon={side === 'front' ? 'docFront' : 'docBack'}
                    accept={ACCEPTED_FILES}
                    maxSizeMb={MAX_SIZE_MB}
                    isEdit={file ? { image: preview, fileName: file.name, isImage } : null}
                    onToggleFile={(files) => onFile(files?.[0] || null)}
                    onDeleteFile={() => onFile(null)}
                    onRejectFile={onReject}
                    dropzoneContent={<DropHint>Clique ou arraste o arquivo</DropHint>}
                />
            </SlotBox>

            {file && <FileName title={file.name}>{file.name}</FileName>}

            {error && (
                <FieldError role='alert'>
                    <AlertCircle size={13} aria-hidden='true' />
                    {error}
                </FieldError>
            )}
        </SlotWrapper>
    );
}

/** Blob URL vive enquanto o arquivo estiver escolhido, e é revogada ao trocar. */
function usePreviews(documents) {
    const [previews, setPreviews] = useState({ front: null, back: null });

    useEffect(() => {
        const urls = {};

        ['front', 'back'].forEach((side) => {
            const file = documents?.[side];
            urls[side] = file ? URL.createObjectURL(file) : null;
        });

        setPreviews(urls);

        return () => {
            Object.values(urls).forEach((url) => url && URL.revokeObjectURL(url));
        };
    }, [documents?.front, documents?.back]);

    return previews;
}

const UploadGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-top: 22px;
`;

const SlotWrapper = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const SlotTitle = styled.p`
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const SlotHint = styled.p`
    margin: 2px 0 8px;
    font-size: 11px;
    line-height: 1.4;
    color: ${colors.night};
`;

const SlotBox = styled.div`
    border-radius: 10px;

    ${({ $invalid }) => $invalid && `
        outline: 1.5px solid ${colors.danger};
        outline-offset: 3px;
    `}
`;

const DropHint = styled.span`
    margin-top: 8px;
    font-size: 11px;
    font-weight: 600;
    color: ${colors.night};
    text-align: center;
`;

const FileName = styled.p`
    margin: 6px 0 0;
    font-size: 11px;
    color: ${colors.night};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const FormatHint = styled.p`
    margin: 10px 0 0;
    font-size: 11px;
    color: ${colors.night};
    text-align: center;
`;

const FieldError = styled.p`
    display: flex;
    align-items: flex-start;
    gap: 5px;
    margin: 6px 0 0;
    font-size: 12px;
    line-height: 1.35;
    color: ${colors.danger};

    svg {
        flex-shrink: 0;
        margin-top: 2px;
    }
`;

const TermsRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-top: 22px;
`;

const TermsCheckbox = styled.input`
    width: 18px;
    height: 18px;
    margin: 1px 0 0;
    accent-color: ${colors.secondaryPurple};
    cursor: pointer;
    flex-shrink: 0;
`;

const TermsLabel = styled.label`
    font-size: 12px;
    line-height: 1.5;
    color: ${colors.nightDark};
    cursor: pointer;
`;

const TermsLink = styled(Link)`
    color: ${colors.secondaryPurple};
    font-weight: 600;
    text-decoration: underline;
`;
